import { useEffect } from 'react';
import { PushNotifications } from '@capacitor/push-notifications';
import { Capacitor } from '@capacitor/core';
import PropTypes from 'prop-types';
import Analytics from '@utils/Analytics';
import store from 'store';
import {
    storeTokenInBackend,
    updatePushInhibitedSetting,
    LOCALSTORAGE_KEY_DEVICE_TOKEN,
} from './PushNotificationPrompt.react';
import { ensureRegistrationListeners } from '@utils/pushNotificationService';

const PushNotificationsHandler = (props, context) => {
    useEffect(() => {
        if (!Capacitor.isPluginAvailable('PushNotifications')) {
            return;
        }

        const platform = Capacitor.getPlatform();
        let receivedListener, actionListener;

        (async () => {
            await ensureRegistrationListeners(
                (token) => {
                    if (token.value && token.value !== store.get(LOCALSTORAGE_KEY_DEVICE_TOKEN)) {
                        store.set(LOCALSTORAGE_KEY_DEVICE_TOKEN, token.value);
                        storeTokenInBackend(token.value, platform);
                    }
                },
                (error) => console.warn('Silent push registration error:', error)
            );

            if (platform === 'ios' || platform === 'android') {
                const storedToken = store.get(LOCALSTORAGE_KEY_DEVICE_TOKEN);
                if (storedToken) {
                    const permissions = await PushNotifications.checkPermissions();

                    if (permissions.receive !== 'granted') {
                        // Sync backend when permission is denied
                        await updatePushInhibitedSetting(true);
                        return;
                    }

                    try {
                        await PushNotifications.register();
                    } catch (err) {
                        console.warn('Silent push token refresh failed:', err);
                    }
                }

                receivedListener = PushNotifications.addListener('pushNotificationReceived', (notification) => {
                    Analytics.trackPushNotificationDelivered({
                        'Notification ID': notification.id,
                        'Device Type': platform, // 'ios', 'android'
                    });
                });

                actionListener = PushNotifications.addListener('pushNotificationActionPerformed', (notification) => {
                    PushNotifications.removeAllDeliveredNotifications();
                    const notificationId = notification.notification.id;
                    const url = notification.notification.data?.url;

                    Analytics.trackPushNotificationOpened({
                        'Notification ID': notificationId,
                        'Device Type': platform, // 'ios', 'android'
                    });

                    if (url) {
                        Analytics.trackPushNotificationAction({
                            'Notification ID': notificationId,
                            'Action Type': 'Navigate', // 'Navigate'
                            'Action Value': url, // When type is Navigate it should be a URL
                        });
                        if (context.router) {
                            // Workaround: If the app is already on /meals, React Router won't detect a navigation change
                            // because it's technically the "same page" with different query params.
                            // To force React Router to recognize the navigation, we first navigate to /my-account
                            // (a different page) and then immediately navigate to the intended URL after a short delay.
                            if (url.startsWith('/meals')) {
                                context.router.push('/my-account');
                            }
                            context.router.push(url);
                        }
                    }
                });
            }
        })();

        return () => {
            receivedListener?.remove();
            actionListener?.remove();
        };
    }, []);

    return null;
};

PushNotificationsHandler.contextTypes = {
    router: PropTypes.object,
};

export default PushNotificationsHandler;
